import { Link } from "@tanstack/react-router";
import { ArrowRight, Menu, X } from "lucide-react";
import { motion, useScroll } from "motion/react";
import { useEffect, useState } from "react";

import icon from "@/assets/images/icon.png";
import { cn } from "@/lib/utils";
import { m } from "@/paraglide/messages.js";

const NAV_LINKS = [
  { label: m.landing_nav_capabilities(), href: "#capabilities" },
  { label: m.landing_nav_how_it_works(), href: "#how-it-works" },
  { label: m.landing_nav_faq(), href: "#faq" },
] as const;

const NAVBAR_WIDTH_INITIAL = "100rem";
const NAVBAR_WIDTH_SCROLLED = "64rem";
const navbarEntranceAnimation = { width: NAVBAR_WIDTH_INITIAL, opacity: 0, y: -24 };
const navbarRestingAnimation = { width: NAVBAR_WIDTH_INITIAL, opacity: 1, y: 0 };
const navbarScrolledAnimation = { width: NAVBAR_WIDTH_SCROLLED, opacity: 1, y: 0 };
// Width reacts to scroll snappily; the opacity/y entrance eases in slower so the
// pill drops in with a deliberate glide instead of snapping over the hero.
const navbarTransition = {
  width: { duration: 0.4, ease: [0.25, 0.1, 0.25, 1] as const },
  default: { duration: 0.8, ease: [0.16, 1, 0.3, 1] as const, delay: 0.1 },
};

/**
 * Marketing navbar for the public landing page. Separate from the
 * authenticated app shell: no sidebar, no org switcher, just a wordmark,
 * anchor links that smooth-scroll to sections, and the sign-up entry point.
 *
 * Mirrors the marketplace navbar: a floating pill with no border at the top of
 * the page that shrinks and picks up a blurred background + border once the
 * user scrolls.
 */
export const LandingNav = () => {
  const { scrollY } = useScroll();
  const [open, setOpen] = useState(false);
  const [hasScrolled, setHasScrolled] = useState(false);

  useEffect(() => {
    const unsubscribe = scrollY.on("change", (latest) => {
      setHasScrolled(latest > 10);
    });

    return unsubscribe;
  }, [scrollY]);

  const handleScroll = (href: string) => {
    setOpen(false);
    const id = href.replace("#", "");
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <header
      data-tour="landing-nav"
      className={cn(
        "sticky z-50 mx-2 flex justify-center transition-all duration-300 md:mx-0",
        hasScrolled ? "top-4" : "top-2",
      )}
    >
      <motion.div
        className="w-full max-w-[calc(100vw-1rem)]"
        initial={navbarEntranceAnimation}
        animate={hasScrolled ? navbarScrolledAnimation : navbarRestingAnimation}
        transition={navbarTransition}
      >
        <div
          className={cn(
            "mx-auto flex h-14 max-w-[1320px] items-center rounded-2xl px-4 transition-all duration-300 md:px-6",
            hasScrolled ? "border border-border bg-background/75 backdrop-blur-lg" : "border border-transparent",
          )}
        >
          <div className="flex flex-1 items-center justify-start">
            <Link to="/" className="flex items-center gap-3">
              <img src={icon} alt="" className="size-6 rounded-xs" />
              <span className="text-base font-semibold tracking-tight text-foreground">{m.app_title()}</span>
            </Link>
          </div>

          <nav className="hidden flex-1 items-center justify-center gap-1 md:flex">
            {NAV_LINKS.map((link) => (
              <button
                key={link.href}
                type="button"
                onClick={() => handleScroll(link.href)}
                className="whitespace-nowrap rounded-full px-4 py-2 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground"
              >
                {link.label}
              </button>
            ))}
          </nav>

          <div className="flex flex-1 items-center justify-end gap-5">
            <Link
              to="/login"
              className="hidden whitespace-nowrap text-sm font-medium text-muted-foreground transition-colors hover:text-foreground md:inline-flex"
            >
              {m.landing_nav_sign_in()}
            </Link>
            <Link
              to="/contact"
              className="group hidden h-9 whitespace-nowrap items-center gap-1.5 rounded-full border border-primary/25 bg-gradient-to-b from-primary/15 to-primary/5 px-5 text-sm font-medium text-primary shadow-sm transition-all hover:border-primary/40 hover:from-primary/20 hover:to-primary/10 md:inline-flex"
            >
              {m.landing_cta_request_demo()}
              <ArrowRight className="size-4 transition-transform group-hover:translate-x-0.5" />
            </Link>
            <button
              type="button"
              aria-label={m.landing_nav_toggle_menu()}
              aria-expanded={open}
              onClick={() => setOpen((v) => !v)}
              className="inline-flex size-9 items-center justify-center rounded-md text-foreground transition-colors hover:bg-accent md:hidden"
            >
              {open ? <X className="size-5" /> : <Menu className="size-5" />}
            </button>
          </div>
        </div>
      </motion.div>

      {open && (
        <div className="fixed inset-x-0 top-20 z-50 mx-auto w-[95%] max-w-md rounded-2xl border border-border bg-background/95 p-3 shadow-2xl backdrop-blur-xl md:hidden">
          <nav className="flex flex-col gap-1">
            {NAV_LINKS.map((link) => (
              <button
                key={link.href}
                type="button"
                onClick={() => handleScroll(link.href)}
                className="rounded-md px-2 py-2 text-left text-sm font-medium text-muted-foreground transition-colors hover:text-foreground"
              >
                {link.label}
              </button>
            ))}
            <Link
              to="/login"
              className={cn(
                "rounded-md px-2 py-2 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground",
              )}
            >
              {m.landing_nav_sign_in()}
            </Link>
            <Link
              to="/contact"
              className="mt-1 inline-flex h-9 items-center justify-center gap-1.5 rounded-full border border-primary/25 bg-gradient-to-b from-primary/15 to-primary/5 px-5 text-sm font-medium text-primary transition-all hover:border-primary/40 hover:from-primary/20 hover:to-primary/10"
            >
              {m.landing_cta_request_demo()}
              <ArrowRight className="size-4" />
            </Link>
          </nav>
        </div>
      )}
    </header>
  );
};
